import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveFaqContentLanguage, USER_ROLE, usesEnglishLegalContent, type FaqAudienceRole, type FaqLocalizedItem } from '@fdh/shared';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { InfoPageShell } from '../../components/layout/InfoPageShell';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { faqApi } from '../../lib/api';
import { APP_ICONS } from '../../lib/iconAssets';

type ExpandState = Record<string, boolean>;

export const FaqPage = () => {
  const { t, language } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<FaqAudienceRole>(USER_ROLE.HELPER);
  const [items, setItems] = useState<FaqLocalizedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandState>({});

  const languageForApi = useMemo(() => resolveFaqContentLanguage(language), [language]);
  const showEnglishContentNotice = usesEnglishLegalContent(language);

  useEffect(() => {
    if (isAuthenticated && user?.role) {
      setActiveTab(user.role);
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    const loadFaqs = async () => {
      setLoading(true);
      setError(null);
      try {
        const roleParam = isAuthenticated && user?.role ? user.role : undefined;
        const res = await faqApi.list({ role: roleParam, language: languageForApi });
        if (res.success && res.data) {
          setItems(res.data.faqs);
          return;
        }
        setError(res.error ?? t('faq.loadError'));
      } catch (e) {
        setError(e instanceof Error ? e.message : t('faq.loadError'));
      } finally {
        setLoading(false);
      }
    };
    void loadFaqs();
  }, [isAuthenticated, languageForApi, t, user?.role]);

  const displayedItems = useMemo(() => {
    if (isAuthenticated && user?.role) {
      return items.filter((item) => item.audienceRole === user.role);
    }
    return items.filter((item) => item.audienceRole === activeTab);
  }, [activeTab, isAuthenticated, items, user?.role]);

  const handleToggle = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const pageContent = (
    <div
      className={
        isAuthenticated
          ? 'min-h-full bg-[#f7f8fb] px-4 py-6 dark:bg-gray-950'
          : 'min-h-full bg-gray-50 px-4 py-8 pb-16'
      }
    >
      <div className="mx-auto max-w-3xl">
        {!isAuthenticated ? (
          <>
            <div className="mb-6 flex items-center justify-between gap-2">
              <Link
                to="/"
                className="text-sm font-medium text-primary-600 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                ← {t('legal.backHome')}
              </Link>
              <LanguageSwitcher />
            </div>
            <span className="flex items-center gap-2 mb-4">
              <img
                src={APP_ICONS.any192}
                alt="MaidEasy logo"
                className="mb-4 h-14 w-14 rounded-lg object-contain"
              /> 
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">MaidEasy - Make.Easy!</h1> <br/>
            </span>

          </>
        ) : null}

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('faq.title')}</h1>

        {showEnglishContentNotice ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
            {t('legal.contentEnglishNotice')}
          </p>
        ) : null}

        {!isAuthenticated ? (
          <div className="mt-5 flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setActiveTab(USER_ROLE.HELPER)}
              className={`min-h-11 flex-1 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                activeTab === USER_ROLE.HELPER
                  ? 'bg-primary-50 text-primary-800'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('faq.tabHelper')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(USER_ROLE.EMPLOYER)}
              className={`min-h-11 flex-1 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                activeTab === USER_ROLE.EMPLOYER
                  ? 'bg-primary-50 text-primary-800'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('faq.tabEmployer')}
            </button>
          </div>
        ) : null}

        {loading ? <p className="mt-6 text-sm text-gray-500">{t('common.loading')}</p> : null}
        {error ? (
          <p className="mt-6 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && !error ? (
          displayedItems.length === 0 ? (
            <p className="mt-6 text-sm text-gray-500">{t('faq.empty')}</p>
          ) : (
            <ul className="mt-6 space-y-3" role="list">
              {displayedItems.map((item) => {
                const opened = expanded[item.id] === true;
                return (
                  <li key={item.id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
                    <button
                      type="button"
                      className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-3 text-left"
                      onClick={() => handleToggle(item.id)}
                      aria-expanded={opened}
                    >
                      <span className="font-medium text-gray-900">{item.question}</span>
                      <span className="text-gray-400" aria-hidden>
                        {opened ? '−' : '+'}
                      </span>
                    </button>
                    {opened ? (
                      <div className="border-t border-gray-100 px-4 py-3 text-sm leading-relaxed text-gray-700">
                        {item.answer}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
      </div>
    </div>
  );

  return <InfoPageShell>{pageContent}</InfoPageShell>;
};
